import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PiArrowCounterClockwise } from 'react-icons/pi';

import { fetchCustomerOrderList } from '#/api/crmMgmt/customerOrder/fetchList';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import { createSelectColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import { buildCustomerOrderPickerColumns } from '#/modules/crmMgmt/customerOrder/data/customerOrderPickerColumns';
import useCustomerOrderPickerListQuery from '#/modules/crmMgmt/customerOrder/hooks/useCustomerOrderPickerListQuery';
import { customerOrderListQueryKey } from '#/utils/queryKeys';

const CUSTOMER_ORDER_PICKER_SORT_FIELD_LABELS = {
  customerOrderNo: '客戶訂單號',
  status: '狀態',
  customerOrderDate: '訂單日',
};

function buildInitialSelection(initialSelectedId = null, rows = []) {
  if (!initialSelectedId) {
    return { rowSelection: {}, selectedRow: null };
  }

  const matched = rows.find((row) => String(row.id) === String(initialSelectedId));
  return {
    rowSelection: { [String(initialSelectedId)]: true },
    selectedRow: matched || { id: initialSelectedId },
  };
}

/**
 * 單選客戶訂單（選填關聯銷貨單；建議 APPROVED／PARTIALLY_SHIPPED／SHIPPED）。
 */
const CustomerOrderPickerDialog = ({
  open,
  onOpenChange,
  initialSelectedId = null,
  initialSelectedRow = null,
  onConfirm,
  title = '選擇客戶訂單（選填）',
  description = '可選填關聯客戶訂單以預填出貨與明細；亦可不選，改於建立頁直接選客戶。',
}) => {
  const {
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetQuery,
  } = useCustomerOrderPickerListQuery();

  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedRow, setSelectedRow] = useState(() => (
    initialSelectedRow || (initialSelectedId ? { id: initialSelectedId } : null)
  ));
  const [rowSelection, setRowSelection] = useState(() => (
    initialSelectedId ? { [String(initialSelectedId)]: true } : {}
  ));

  const listQueryResult = useQuery({
    queryKey: [...customerOrderListQueryKey(listQuery), 'picker'],
    queryFn: () => fetchCustomerOrderList(listQuery),
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(
    () => listQueryResult.data?.data ?? [],
    [listQueryResult.data?.data],
  );

  const columns = useMemo(() => ([
    createSelectColumn({ enableMultiSelection: false, showHeaderCheckbox: false }),
    ...buildCustomerOrderPickerColumns(),
  ]), []);

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns),
    [columns],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowSelectionChange = useCallback((selection) => {
    setRowSelection(selection);
    const selectedId = Object.keys(selection).find((key) => selection[key]);
    if (!selectedId) {
      setSelectedRow(null);
      return;
    }
    const fromPage = rows.find((row) => String(row.id) === String(selectedId));
    setSelectedRow(fromPage || selectedRow || { id: selectedId });
  }, [rows, selectedRow]);

  function handleSearchSubmit() {
    setKeyword(searchKeyword);
  }

  function handleResetFilters() {
    setSearchKeyword('');
    resetQuery();
  }

  function handleConfirm() {
    onConfirm(selectedRow || null);
    onOpenChange(false);
  }

  function handleCancel() {
    onOpenChange(false);
  }

  function handleOpenChange(nextOpen) {
    if (!nextOpen) {
      resetQuery();
      setSearchKeyword('');
      const initial = buildInitialSelection(initialSelectedId, rows);
      setRowSelection(initial.rowSelection);
      setSelectedRow(initialSelectedRow || initial.selectedRow);
    }
    onOpenChange(nextOpen);
  }

  const headerSearch = (
    <div className="flex w-full max-w-md items-center gap-2 sm:w-72">
      <TableSearchInput
        useKeyword
        keyword={searchKeyword}
        onKeywordChange={setSearchKeyword}
        onSearch={handleSearchSubmit}
        placeholder="搜尋客訂單號、備註"
        className="min-w-0 w-full text-foreground"
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className={cn(
              'hover:text-gray-50',
              'size-10 shrink-0',
            )}
            aria-label="清除搜尋與篩選"
            onClick={handleResetFilters}
          >
            <PiArrowCounterClockwise size={20} aria-hidden />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">清除搜尋與篩選</TooltipContent>
      </Tooltip>
    </div>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      description={description}
      headerVariant="primary"
      headerEnd={headerSearch}
      showCloseButton={false}
      maxWidthClassName={dialogWideMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      closeOnOutsideClick={false}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmText="確認選取"
      cancelText="取消"
      cancelAriaLabel="取消選客訂"
      confirmDisabled={listQueryResult.isLoading || !selectedRow?.id}
    >
      <ListTablePanel
        variant="plain"
        columns={columns}
        rows={rows}
        pagination={listQueryResult.data?.page ?? {
          current_page: 1,
          page_size: 10,
          total_count: 0,
          total_page: 1,
        }}
        isLoading={listQueryResult.isLoading}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        listQuery={listQuery}
        filterStatusFields={filterStatusFields}
        sortFieldLabels={CUSTOMER_ORDER_PICKER_SORT_FIELD_LABELS}
        onFilterChange={setFilter}
        onKeywordClear={() => {
          setSearchKeyword('');
          setKeyword('');
        }}
        onSortRemove={removeSortAt}
        onSortingChange={handleSortingChange}
        enableMultiSelection={false}
        paginationClassName="px-0 py-2"
        initialRowSelection={rowSelection}
        onRowSelectionChange={handleRowSelectionChange}
      />
    </AppDialog>
  );
};

CustomerOrderPickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  initialSelectedId: PropTypes.string,
  initialSelectedRow: PropTypes.shape({
    id: PropTypes.string,
    customerOrderNo: PropTypes.string,
    status: PropTypes.string,
  }),
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
};

export default CustomerOrderPickerDialog;
